/**
 * EA Report Calculation Functions
 *
 * These functions duplicate the logic from ProjectEAReportPreview.tsx
 * to enable the new EA Report implementation without modifying existing files.
 *
 * NOTE: This is a duplication of logic. In the future, these functions should
 * be extracted to a shared utility file that both components can use.
 *
 * Functions copied from ProjectEAReportPreview.tsx:
 * - formatMeters (lines ~63-266)
 * - calculateOffersForMeters (lines ~268-744)
 */

import {
  flatMap,
  get,
  groupBy,
  isEqual,
  last,
  multiply,
  pick,
  reverse,
  sortBy,
  sum,
  sumBy,
  uniq,
  uniqBy,
  uniqWith
} from 'lodash-es';
import { DAYS_PER_YEAR } from '~/constants/date';
import { ILookupPrimaryResponse } from '~/features/lookup/models/lookup';
import { date, formatApiDate, getEarlierDate, isDateBetween } from '~/lib/date';
import { calculateDailyEnergy } from '~/models/common';
import { NMI } from '~/models/meter';
import {
  GreenPricingType,
  GreenPricingTypes,
  IContractRatePeriod,
  IProjectOfferPrimaryResponse,
  ProjectOffer,
  ProjectOfferPeriod
} from '~/models/project';
import { GreenBillingCalculation } from '~/models/retailer';
import { calculateWeightedRatesByEnergyFromMeters } from '../weightedRateUtils';
import {
  aggregatePeriodCosts,
  calculateEffectiveRateUnitWeightedAvg,
  calculateRateUnitWeightedAvgFromMergedPeriods,
  calculateWeightedEnvironmentalRates
} from './aggregationHelpers';
import { calculateMeterConsumption, calculateMeterConsumptionByComponent } from './meterOfferCalculationUtils';
import {
  doDateRangesOverlap,
  findBestMatchingEnvironmentalPeriod,
  findOverlappingEnvironmentalPeriods,
  mergeContractAndEnvironmentalRates
} from './periodCalculationHelpers';
import {
  CalculatedProjectOffer,
  CalculatedProjectOfferPeriod,
  CalculatedProjectOfferPeriodsByName,
  EAReportMeter,
  ReportMeterWeighedAverageData,
  ReportProjectCalculatedMeters,
  ReportProjectMeter
} from './reportTypes';

/**
 * Get loss factor based on greenBilling setting.
 * DLF_MLF: dlf × mlf; MLF: mlf only; DLF (default): dlf only.
 */
export function getLossFactorFromGreenBilling(
  greenBilling: GreenBillingCalculation | undefined,
  dlf: number,
  mlf: number
): number {
  if (greenBilling === GreenBillingCalculation.DLF_MLF) return dlf * mlf;
  if (greenBilling === GreenBillingCalculation.MLF) return mlf;
  return dlf;
}

/**
 * Split a date range into calendar-year segments with day counts.
 * Returns [{ year, days }, ...] for each year the range touches.
 */
function splitOverlapByYear(overlapStart: string, overlapEnd: string): { year: number; days: number }[] {
  const start = date(overlapStart);
  const end = date(overlapEnd);
  const segments: { year: number; days: number }[] = [];
  let current = start;

  while (!current.isAfter(end, 'day')) {
    const year = current.year();
    const yearEndStr = `${year}-12-31`;
    const segmentEndStr = getEarlierDate(yearEndStr, formatApiDate(end));
    const segmentEndDate = date(segmentEndStr);
    // Inclusive days: segmentEnd - current + 1 (dayjs diff = first - second)
    const days = segmentEndDate.diff(current, 'day') + 1;
    if (days > 0) segments.push({ year, days });
    current = segmentEndDate.add(1, 'day');
  }

  return segments;
}

/**
 * Split a segment by calendar year into sub-segments with date ranges.
 * Used so env rate (MAT_CERT/CAL_CERT) is computed per year from lookup, not weighted across years.
 * Returns [{ start, end, year, days }, ...].
 */
function splitSegmentByCalendarYear(
  segmentStart: string,
  segmentEnd: string
): { start: string; end: string; year: number; days: number }[] {
  const start = date(segmentStart);
  const end = date(segmentEnd);
  const segments: { start: string; end: string; year: number; days: number }[] = [];
  let current = start;

  while (!current.isAfter(end, 'day')) {
    const year = current.year();
    const yearEndStr = `${year}-12-31`;
    const segmentEndDate = getEarlierDate(yearEndStr, formatApiDate(end));
    const segmentEnd = date(segmentEndDate);
    const days = segmentEnd.diff(current, 'day') + 1;
    if (days > 0) {
      segments.push({
        start: formatApiDate(current),
        end: formatApiDate(segmentEnd),
        year,
        days
      });
    }
    current = segmentEnd.add(1, 'day');
  }

  return segments;
}

/**
 * Calculate environmental rate (c/kWh) for EA Report.
 *
 * Step 1 – Match env period:
 *   - Find env periods overlapping segment (start/end)
 *   - Filter by contract periodName (e.g. 36M → periodName=36)
 *   - If multiple: pick largest endDate, tiebreaker same periodName
 *
 * Step 2 – Lookup date range:
 *   - Use overlap (segment ∩ env period), NOT full env period
 *
 * Step 3 – Rate per year:
 *   - rawValue = matchedEnvPeriod[greenPriceType] (from offer env period)
 *   - lookupPct = lookupByYear.get(year)?.[greenPriceType] (from lookup table)
 *   - rateForYear = lookupPct > 0 ? (rawValue * lookupPct) / 1000 : rawValue / 10
 *
 * Step 4 – Rate:
 *   - Segments are split by calendar year at build time, so segment range is at most one year; use that year's rate (no weighting).
 *   - If overlap still spans years (legacy path), result = sum(rateForYear * days) / totalDays.
 */
function calculateEAReportGreenPrice(
  greenPriceType: GreenPricingType,
  segmentStart: string,
  segmentEnd: string,
  offer: ProjectOffer,
  greenPriceLookUps: ILookupPrimaryResponse[] = [],
  contractPeriodName?: number
): number {
  const environmentalPeriods = offer.environmentalRatePeriods || [];
  if (environmentalPeriods.length === 0) return 0;

  const overlappingEnvPeriods = findOverlappingEnvironmentalPeriods(
    { startDate: segmentStart, endDate: segmentEnd } as IContractRatePeriod,
    environmentalPeriods
  );
  const matchedEnvPeriod = findBestMatchingEnvironmentalPeriod(
    { start: segmentStart, end: segmentEnd },
    overlappingEnvPeriods,
    contractPeriodName
  );

  if (!matchedEnvPeriod) return 0;

  const rawValue = matchedEnvPeriod[greenPriceType] ?? 0;
  const pricingType = matchedEnvPeriod[`${greenPriceType}Type` as keyof typeof matchedEnvPeriod] as
    | GreenPricingTypes
    | undefined;

  // CAL_RATE / MAT_RATE: value in env period is already the rate (c/kWh). Use as-is, no lookup.
  // Same for PDRS, REPS or any non-CERT type (old logic: only CAL_CERT/MAT_CERT use lookup; else return period[greenPriceType]).
  const isCertType = pricingType === GreenPricingTypes.CAL_CERT || pricingType === GreenPricingTypes.MAT_CERT;
  if (!isCertType) {
    return rawValue;
  }

  // CAL_CERT / MAT_CERT: convert cert to rate using lookup table (rate = lookupPct * rawValue / 1000, or rawValue/10 if no lookup)
  const lookupByYear = greenPriceLookUps.reduce((map, l) => {
    if (!map.has(l.year)) map.set(l.year, l);
    return map;
  }, new Map<number, ILookupPrimaryResponse>());

  const segmentStartVal = date(segmentStart).valueOf();
  const segmentEndVal = date(segmentEnd).valueOf();
  const envStartVal = date(matchedEnvPeriod.startDate).valueOf();
  const envEndVal = date(matchedEnvPeriod.endDate).valueOf();
  const overlapStart = formatApiDate(date(Math.max(segmentStartVal, envStartVal)));
  const overlapEnd = formatApiDate(date(Math.min(segmentEndVal, envEndVal)));
  const yearSegments = splitOverlapByYear(overlapStart, overlapEnd);

  let totalWeightedRate = 0;
  let totalOverlapDays = 0;

  if (yearSegments.length > 0) {
    for (const { year, days } of yearSegments) {
      const lookupPct = lookupByYear.get(year)?.[greenPriceType] ?? 0;
      const rateForYear = lookupPct > 0 ? (rawValue * lookupPct) / 1000 : rawValue / 10;
      totalWeightedRate += rateForYear * days;
      totalOverlapDays += days;
    }
  } else {
    const overlapDays = date(overlapEnd).diff(date(overlapStart), 'day') + 1;
    totalWeightedRate = (rawValue / 10) * overlapDays;
    totalOverlapDays = overlapDays;
  }

  return totalOverlapDays > 0 ? totalWeightedRate / totalOverlapDays : 0;
}

/**
 * Format meters for calculation
 * Duplicated from ProjectEAReportPreview.formatMeters
 */
export function formatMeters(data: EAReportMeter[]): ReportProjectCalculatedMeters {
  const projectMeters = data
    .filter((v) => !!v)
    .map((meter) => {
      const annualPeakEnergy = get(meter, 'annualEstPeakEnergy') || get(meter.contract, 'contractPeakEnergy');
      const annualOffPeakEnergy = get(meter, 'annualEstOffPeakEnergy') || get(meter.contract, 'contractOffPeakEnergy');
      const annualShoulderEnergy =
        get(meter, 'annualEstShoulderEnergy') || get(meter.contract, 'contractShoulderEnergy');
      return {
        ...meter,
        annualData: {
          peakEnergy: annualPeakEnergy,
          offPeakEnergy: annualOffPeakEnergy,
          shoulderEnergy: annualShoulderEnergy,
          totalEnergy: sum([annualPeakEnergy ?? 0, annualOffPeakEnergy ?? 0, annualShoulderEnergy ?? 0]) || 0,
          retailerFeePerMeter: get(meter, 'contract.retailSupplyCharge', 0) ?? 0,
          meteringChargePerMeter:
            (Number(get(meter, 'contract.meteringCharge')) || 0) +
            (Number(get(meter, 'contract.meteringServiceFeePrice')) || 0)
        }
      };
    });

  if (!projectMeters || projectMeters?.length === 0) {
    return {} as ReportProjectCalculatedMeters;
  }

  const currProjectMeterContract = projectMeters[0].contract;
  const annualTotalEnergyAcrossAllSites = sumBy(projectMeters, 'annualData.totalEnergy');
  const {
    peak: weightedPeakRate,
    offPeak: weightedOffPeakRate,
    shoulder: weightedShoulderRate
  } = calculateWeightedRatesByEnergyFromMeters(projectMeters);
  const weighedLretRate =
    sumBy(projectMeters, (v) => multiply(v.annualData.totalEnergy ?? 0, v.contractPeriod?.lretRate ?? 0)) /
    annualTotalEnergyAcrossAllSites;
  const weighedVeetRate =
    sumBy(projectMeters, (v) => multiply(v.annualData.totalEnergy ?? 0, v.contractPeriod?.veetRate ?? 0)) /
    annualTotalEnergyAcrossAllSites;
  const weighedEssRate =
    sumBy(projectMeters, (v) => multiply(v.annualData.totalEnergy ?? 0, v.contractPeriod?.essRate ?? 0)) /
    annualTotalEnergyAcrossAllSites;
  const weighedSrecRate =
    sumBy(projectMeters, (v) => multiply(v.annualData.totalEnergy ?? 0, v.contractPeriod?.srecRate ?? 0)) /
    annualTotalEnergyAcrossAllSites;
  const weighedAeeisRate =
    sumBy(projectMeters, (v) => multiply(v.annualData.totalEnergy ?? 0, v.contractPeriod?.aeeisRate ?? 0)) /
    annualTotalEnergyAcrossAllSites;
  // Note: pdrsRate and repsRate may not exist in ContractPeriod (current contract data)
  // These are set to 0 for current contract calculations
  const weighedPdrsRate = 0; // Current contract may not have pdrsRate
  const weighedRepsRate = 0; // Current contract may not have repsRate
  const weighedAncillaryCharge =
    sumBy(projectMeters, (v) => multiply(v.annualData.totalEnergy ?? 0, v.ancillaryCharge ?? 0)) /
    annualTotalEnergyAcrossAllSites;
  const weighedParticipantCharge =
    sumBy(projectMeters, (v) => multiply(v.annualData.totalEnergy ?? 0, v.participantCharge ?? 0)) /
    annualTotalEnergyAcrossAllSites;
  const weighedAnnualRetailerFee = sumBy(projectMeters, (v) => v.annualData.retailerFeePerMeter) / projectMeters.length;
  const weighedAnnualMeteringCharge =
    sumBy(projectMeters, (v) => v.annualData.meteringChargePerMeter) / projectMeters.length;

  const lwaDLF =
    sumBy(projectMeters, (meter) => (meter.dlf || meter.tlf || 1) * meter.annualData.totalEnergy) /
    annualTotalEnergyAcrossAllSites;
  const lwaMLF =
    sumBy(projectMeters, (meter) => (meter.mlf || meter.tlf || 1) * meter.annualData.totalEnergy) /
    annualTotalEnergyAcrossAllSites;
  const lossFactor = getLossFactorFromGreenBilling(currProjectMeterContract.retailer?.greenBilling, lwaDLF, lwaMLF);

  const calculatedMeters = projectMeters
    .map((meter) => ({
      ...meter,
      annualData: {
        ...meter.annualData,
        networkStanding: NMI.from(meter).calculateAnnualNetworkStanding(),
        networkChargesEnergy: NMI.from(meter).calculateAnnualNetworkChargesEnergy(meter.annualData.totalEnergy),
        networkChargesDemand: NMI.from(meter).calculateAnnualNetworkChargesDemand()
      }
    }))
    .map((meter) => {
      const lossFactor = getLossFactorFromGreenBilling(
        meter.contract.retailer?.greenBilling,
        meter.dlf ?? meter.tlf ?? 1,
        meter.mlf ?? meter.tlf ?? 1
      );
      // Energy cost: per-meter (quantity_inc_loss * rate) / 100, same as CurrentVsNewSection and Quantity Unit (inc loss)
      // Uses dlf×mlf via calculateMeterConsumptionByComponent (usePerMeterLossFactor: true)
      const comp = calculateMeterConsumptionByComponent(meter as ReportProjectMeter, DAYS_PER_YEAR, {
        usePerMeterLossFactor: true
      });
      const energyCost =
        (comp.peak * (meter.contractPeriod.peakRate ?? 0) +
          comp.offPeak * (meter.contractPeriod.offPeakRate ?? 0) +
          comp.shoulder * (meter.contractPeriod.shoulderRate ?? 0)) /
        100;
      const renewableCost =
        (sum([
          meter.contractPeriod?.aeeisRate ?? 0,
          meter.contractPeriod?.essRate ?? 0,
          meter.contractPeriod?.lretRate ?? 0,
          meter.contractPeriod?.veetRate ?? 0,
          meter.contractPeriod?.srecRate ?? 0,
          (meter.contractPeriod as { pdrsRate?: number })?.pdrsRate ?? 0,
          (meter.contractPeriod as { repsRate?: number })?.repsRate ?? 0
        ]) *
          meter.annualData.totalEnergy *
          lossFactor) /
        100;
      const regulatedCost =
        (sum([meter.ancillaryCharge ?? 0, meter.participantCharge ?? 0]) * meter.annualData.totalEnergy * lossFactor) /
        100;
      const annualRetailerFee = (meter.annualData.retailerFeePerMeter ?? 0) * meter.numOfMeters;
      const annualMeteringCharge = (meter.annualData.meteringChargePerMeter ?? 0) * meter.numOfMeters;
      const contestableCost = sum([energyCost, renewableCost, annualRetailerFee, annualMeteringCharge]);

      return {
        ...meter,
        annualData: {
          ...meter.annualData,
          lossFactor,
          energyCost,
          renewableCost,
          regulatedCost,
          annualRetailerFee,
          annualMeteringCharge,
          contestableCost
        }
      };
    });

  // Use sum of per-meter costs for consistency (handles mixed TARIFF/C&I, different loss factors)
  const currentEnergyCost = sumBy(calculatedMeters, 'annualData.energyCost');
  const currentRenewableCost = sumBy(calculatedMeters, 'annualData.renewableCost');
  const currentRegulatedCost = sumBy(calculatedMeters, 'annualData.regulatedCost');
  // Sum per-meter: each meter's (rate * numOfMeters) to support groups with different meter counts/rates
  const currentAnnualRetailerFee = sumBy(
    calculatedMeters,
    (m) => (m.annualData.retailerFeePerMeter ?? 0) * m.numOfMeters
  );
  const currentAnnualMeteringCharge = sumBy(
    calculatedMeters,
    (m) => (m.annualData.meteringChargePerMeter ?? 0) * m.numOfMeters
  );

  const currentContestableCost = sum([
    currentEnergyCost,
    currentRenewableCost,
    currentAnnualRetailerFee,
    currentAnnualMeteringCharge
  ]);
  // Use total energy with loss for c/kWh (consistent with contestable cost calculation)
  const annualTotalEnergyWithLoss = sumBy(
    calculatedMeters,
    (m) => (m.annualData.totalEnergy ?? 0) * (m.annualData.lossFactor ?? 1)
  );
  const currentAvgCostPerKwh =
    annualTotalEnergyWithLoss > 0 ? (currentContestableCost / annualTotalEnergyWithLoss) * 100 : 0;

  return {
    meters: calculatedMeters as ReportProjectMeter[],
    lwaDLF,
    lwaMLF,
    lossFactor,
    annualData: {
      totalEnergy: annualTotalEnergyAcrossAllSites,
      networkStanding: sumBy(calculatedMeters, 'annualData.networkStanding'),
      networkChargesEnergy: sumBy(calculatedMeters, 'annualData.networkChargesEnergy'),
      networkChargesDemand: sumBy(calculatedMeters, 'annualData.networkChargesDemand'),
      retailerFee: currentAnnualRetailerFee,
      meteringCharge: currentAnnualMeteringCharge
    },
    weighedAverageData: {
      peakRate: weightedPeakRate,
      offPeakRate: weightedOffPeakRate,
      shoulderRate: weightedShoulderRate,
      lret: weighedLretRate,
      sres: weighedSrecRate,
      ess: weighedEssRate,
      veet: weighedVeetRate,
      aeeis: weighedAeeisRate,
      pdrs: weighedPdrsRate,
      reps: weighedRepsRate,
      ancillaryCharge: weighedAncillaryCharge,
      participantCharge: weighedParticipantCharge,
      annualRetailerFee: weighedAnnualRetailerFee,
      annualMeteringCharge: weighedAnnualMeteringCharge
    },
    currentEnergyCost,
    currentRegulatedCost,
    currentRenewableCost,
    currentContestableCost,
    currentAvgCostPerKwh
  };
}
/**
 * Calculate offers for meters
 * Duplicated from ProjectEAReportPreview.calculateOffersForMeters
 */
export function calculateOffersForMeters({
  projectMeters,
  projectRawOffers,
  greenPriceLookUps = [],
  lwaDLF: _lwaDLF = 1,
  lwaMLF: _lwaMLF = 1,
  weighedAverageData: _weighedAverageData
}: {
  projectMeters: ReportProjectMeter[];
  projectRawOffers: (IProjectOfferPrimaryResponse & { isBest?: boolean })[];
  greenPriceLookUps?: ILookupPrimaryResponse[];
  lwaDLF: number;
  lwaMLF: number;
  weighedAverageData: ReportMeterWeighedAverageData;
}): CalculatedProjectOffer[] {
  if (!projectMeters || projectMeters?.length === 0) {
    return [] as CalculatedProjectOffer[];
  }

  // Calculate costs for a single meter and period
  const calculateSingleMeterPeriodCosts = (
    offer: ProjectOffer,
    period: ProjectOfferPeriod,
    meter: ReportProjectMeter
  ) => {
    const lossFactor = getLossFactorFromGreenBilling(
      offer.greenBilling,
      meter.dlf ?? meter.tlf ?? 1,
      meter.mlf ?? meter.tlf ?? 1
    );

    const lret = calculateEAReportGreenPrice(
      'lret',
      period.startDate,
      period.endDate,
      offer,
      greenPriceLookUps,
      period.periodName
    );
    const sres = calculateEAReportGreenPrice(
      'sres',
      period.startDate,
      period.endDate,
      offer,
      greenPriceLookUps,
      period.periodName
    );
    const ess = calculateEAReportGreenPrice(
      'ess',
      period.startDate,
      period.endDate,
      offer,
      greenPriceLookUps,
      period.periodName
    );
    const veet = calculateEAReportGreenPrice(
      'veet',
      period.startDate,
      period.endDate,
      offer,
      greenPriceLookUps,
      period.periodName
    );
    const aeeis = calculateEAReportGreenPrice(
      'aeeis',
      period.startDate,
      period.endDate,
      offer,
      greenPriceLookUps,
      period.periodName
    );
    const pdrs = calculateEAReportGreenPrice(
      'pdrs',
      period.startDate,
      period.endDate,
      offer,
      greenPriceLookUps,
      period.periodName
    );
    const reps = calculateEAReportGreenPrice(
      'reps',
      period.startDate,
      period.endDate,
      offer,
      greenPriceLookUps,
      period.periodName
    );

    // Energy cost: per-meter (quantity_inc_loss * rate) / 100, same as formatMeters and CurrentVsNewSection
    // Uses meter's own dlf×mlf via calculateMeterConsumptionByComponent
    const comp = calculateMeterConsumptionByComponent(meter, period.numOfDays ?? 0, {
      usePerMeterLossFactor: true
    });
    const energyCost =
      (comp.peak * (period.peak ?? 0) + comp.offPeak * (period.offPeak ?? 0) + comp.shoulder * (period.shoulder ?? 0)) /
      100;

    const dailyTotalEnergy =
      calculateDailyEnergy(meter.annualData.peakEnergy) +
      calculateDailyEnergy(meter.annualData.shoulderEnergy) +
      calculateDailyEnergy(meter.annualData.offPeakEnergy);

    const renewableCost =
      (sum([lret, sres, ess, veet, aeeis, pdrs, reps]) * dailyTotalEnergy * lossFactor * period.numOfDays) / 100;

    // Use meter's own ancillaryCharge and participantCharge, not weighted average
    const regulatedCost =
      (sum([meter.ancillaryCharge ?? 0, meter.participantCharge ?? 0]) *
        dailyTotalEnergy *
        period.numOfDays *
        lossFactor) /
      100;

    const networkCost =
      (sum([
        meter.annualData.networkStanding,
        meter.annualData.networkChargesEnergy,
        meter.annualData.networkChargesDemand
      ]) /
        DAYS_PER_YEAR) *
      period.numOfDays;

    // Metering and Retailer Charges: Respect data source
    // If offer provides meteringFeePrice/serviceFeePrice (even if 0), use the calculated daily fee
    // Otherwise, use annual price from meter data and allocate proportionally by segment days
    // Must use period.numOfDays/DAYS_PER_YEAR (not periodName/12) since this is called per SEGMENT
    const annualMeteringChargePerMeter = meter.annualData.meteringChargePerMeter ?? 0;
    const annualRetailerFeePerMeter = meter.annualData.retailerFeePerMeter ?? 0;

    const meteringCharge =
      offer.meteringFeePrice != null
        ? offer.dailyMeteringFee * period.numOfDays * meter.numOfMeters
        : ((annualMeteringChargePerMeter * period.numOfDays) / DAYS_PER_YEAR) * meter.numOfMeters;

    const retailerServiceFee =
      offer.serviceFeePrice != null
        ? offer.dailyServiceFee * period.numOfDays * meter.numOfMeters
        : ((annualRetailerFeePerMeter * period.numOfDays) / DAYS_PER_YEAR) * meter.numOfMeters;

    const contestableCost = sum([energyCost, renewableCost, meteringCharge, retailerServiceFee]);

    return {
      energyCost,
      renewableCost,
      regulatedCost,
      networkCost,
      meteringCharge,
      retailerServiceFee,
      contestableCost,
      totalCosts: contestableCost + networkCost + regulatedCost
    };
  };

  // Calculate costs for meters in this offer
  // Note: For comparison in BestOfferSection, we need to calculate costs for ALL projectMeters
  // to ensure all offers in the same group can be compared on the same basis
  // However, for aggregation, we only sum costs for meters that are actually covered by this offer
  const calculatePeriodWithCosts = (offer: ProjectOffer, period: ProjectOfferPeriod) => {
    // Calculate costs for ALL projectMeters (for fair comparison in BestOfferSection)
    // But only aggregate costs for meters that are actually covered by this offer
    const meterCosts = projectMeters.map((meter) => calculateSingleMeterPeriodCosts(offer, period, meter));

    // Filter meter costs to only include meters covered by this offer
    const offerMeterCosts = projectMeters
      .map((meter, index) => ({ meter, cost: meterCosts[index] }))
      .filter(({ meter }) => offer.meterIds.includes(meter.id))
      .map(({ cost }) => cost);

    // Store per-meter costs (for Mix & Match and Offer Comparison; avoid proportional allocation)
    const meterContestableCosts: Record<string, number> = {};
    const meterEnergyCosts: Record<string, number> = {};
    const meterRenewableCosts: Record<string, number> = {};
    const meterRetailerServiceFee: Record<string, number> = {};
    const meterMeteringCharge: Record<string, number> = {};
    projectMeters.forEach((meter, index) => {
      if (offer.meterIds.includes(meter.id)) {
        const c = meterCosts[index];
        meterContestableCosts[meter.id] = c.contestableCost;
        meterEnergyCosts[meter.id] = c.energyCost;
        meterRenewableCosts[meter.id] = c.renewableCost;
        meterRetailerServiceFee[meter.id] = c.retailerServiceFee;
        meterMeteringCharge[meter.id] = c.meteringCharge;
      }
    });

    // Calculate green prices for the period (used in the period object)
    // Note: These are calculated at the period level, while renewableCost is already aggregated from meters
    const lret = calculateEAReportGreenPrice(
      'lret',
      period.startDate,
      period.endDate,
      offer,
      greenPriceLookUps,
      period.periodName
    );
    const sres = calculateEAReportGreenPrice(
      'sres',
      period.startDate,
      period.endDate,
      offer,
      greenPriceLookUps,
      period.periodName
    );
    const ess = calculateEAReportGreenPrice(
      'ess',
      period.startDate,
      period.endDate,
      offer,
      greenPriceLookUps,
      period.periodName
    );
    const veet = calculateEAReportGreenPrice(
      'veet',
      period.startDate,
      period.endDate,
      offer,
      greenPriceLookUps,
      period.periodName
    );
    const aeeis = calculateEAReportGreenPrice(
      'aeeis',
      period.startDate,
      period.endDate,
      offer,
      greenPriceLookUps,
      period.periodName
    );
    const pdrs = calculateEAReportGreenPrice(
      'pdrs',
      period.startDate,
      period.endDate,
      offer,
      greenPriceLookUps,
      period.periodName
    );
    const reps = calculateEAReportGreenPrice(
      'reps',
      period.startDate,
      period.endDate,
      offer,
      greenPriceLookUps,
      period.periodName
    );

    // Consumption (inc loss) per segment – sum over offer meters for tooltip display
    const offerMeters = projectMeters.filter((m) => offer.meterIds.includes(m.id));
    const segmentConsumption =
      offerMeters.length > 0
        ? offerMeters.reduce(
            (acc, meter) => {
              const comp = calculateMeterConsumptionByComponent(meter as ReportProjectMeter, period.numOfDays ?? 0, {
                usePerMeterLossFactor: true
              });
              acc.peak += comp.peak;
              acc.offPeak += comp.offPeak;
              acc.shoulder += comp.shoulder;
              acc.total += comp.total;
              return acc;
            },
            { peak: 0, offPeak: 0, shoulder: 0, total: 0 }
          )
        : undefined;
    // Consumption (without loss) per segment – display only
    const segmentConsumptionExcludeLoss =
      offerMeters.length > 0
        ? offerMeters.reduce(
            (acc, meter) => {
              const comp = calculateMeterConsumptionByComponent(meter as ReportProjectMeter, period.numOfDays ?? 0, {
                usePerMeterLossFactor: false
              });
              acc.peak += comp.peak;
              acc.offPeak += comp.offPeak;
              acc.shoulder += comp.shoulder;
              acc.total += comp.total;
              return acc;
            },
            { peak: 0, offPeak: 0, shoulder: 0, total: 0 }
          )
        : undefined;

    return {
      ...period,
      numOfDays: period.numOfDays,
      startYear: period.startYear,
      endYear: period.endYear,
      daysStart: period.daysStart,
      daysEnd: period.daysEnd,
      lret,
      sres,
      ess,
      veet,
      aeeis,
      pdrs,
      reps,
      ...(segmentConsumption && {
        peakConsumptionIncLoss: segmentConsumption.peak,
        offPeakConsumptionIncLoss: segmentConsumption.offPeak,
        shoulderConsumptionIncLoss: segmentConsumption.shoulder,
        totalConsumptionIncLoss: segmentConsumption.total
      }),
      ...(segmentConsumptionExcludeLoss && {
        peakConsumptionExcludeLoss: segmentConsumptionExcludeLoss.peak,
        offPeakConsumptionExcludeLoss: segmentConsumptionExcludeLoss.offPeak,
        shoulderConsumptionExcludeLoss: segmentConsumptionExcludeLoss.shoulder,
        totalConsumptionExcludeLoss: segmentConsumptionExcludeLoss.total
      }),
      // Only aggregate costs for meters that are actually covered by this offer
      // This ensures each meter's cost is calculated using its own offer values, not weighted average
      energyCost: sumBy(offerMeterCosts, 'energyCost'),
      renewableCost: sumBy(offerMeterCosts, 'renewableCost'),
      regulatedCost: sumBy(offerMeterCosts, 'regulatedCost'),
      networkCost: sumBy(offerMeterCosts, 'networkCost'),
      meteringCharge: sumBy(offerMeterCosts, 'meteringCharge'),
      retailerServiceFee: sumBy(offerMeterCosts, 'retailerServiceFee'),
      contestableCost: sumBy(offerMeterCosts, 'contestableCost'),
      totalCosts: sumBy(offerMeterCosts, 'totalCosts'),
      meterContestableCosts,
      meterEnergyCosts,
      meterRenewableCosts,
      meterRetailerServiceFee,
      meterMeteringCharge
    } as CalculatedProjectOfferPeriod;
  };

  const projectOffers = projectRawOffers.map((rawOffer) => {
    const offer = ProjectOffer.from(rawOffer);

    const contractPeriods = offer.contractRatePeriods || [];
    const environmentalPeriods = offer.environmentalRatePeriods || [];

    // Use only THIS offer's date boundaries for segment splitting (not all offers).
    // This ensures EA Report Preview (synthetic offer per retailer) and Offer Comparison (per-offer)
    // produce the same segment boundaries and costs for the same offer/period.

    // Split each contract period into segments based on:
    // 1. Contract periods from the same offer within the true date range (to get contract rates)
    // 2. Environmental periods that further split segments by environmental rate boundaries
    //
    // Logic:
    // Step 1: For each contractPeriod, calculate trueStartDate = endDate - periodName months + 1 day
    // Step 2: Find all contract periods from the same offer within [trueStartDate, endDate]
    // Step 3: Collect boundary dates from these contract periods and environmental periods
    // Step 4: Create segments based on these boundaries
    // Step 5: Match contract period and environmental period for each segment

    // Group contract periods by periodName FIRST. Each periodName produces ONE output period.
    // For periods with the same periodName, we need to aggregate all their segments together
    const periodsByPeriodName = groupBy(
      contractPeriods.map((period, index) => ({ period, index })),
      ({ period }) => period.periodName
    );

    const periods = Object.entries(periodsByPeriodName).map(([periodNameStr, periodIndexPairs]) => {
      const periodName = Number(periodNameStr);
      const periodsInGroup = periodIndexPairs.map(({ period }) => period);
      const basePeriod = periodIndexPairs[0].period;

      // True date range: rangeStart = earliest start across ALL contract periods of the offer; rangeEnd = max(endDate) of this period group
      const offerEarliestStart = (contractPeriods || []).reduce(
        (min, p) => (!min || date(p.startDate).valueOf() < date(min).valueOf() ? p.startDate : min),
        undefined as string | undefined
      );
      const latestEndDate = periodsInGroup.reduce(
        (max, p) => (!max || date(p.endDate).valueOf() > date(max).valueOf() ? p.endDate : max),
        undefined as string | undefined
      );
      const rangeEnd = latestEndDate || basePeriod.endDate;
      const rangeStart = offerEarliestStart || basePeriod.startDate;

      // Use only periods that overlap this periodName's range so segment boundaries match the contract (e.g. 18M
      // gets segments from 6M and 18M blocks that overlap [rangeStart, rangeEnd], not just one global dedupe).
      const periodsOverlappingRange = [...(contractPeriods || []), ...(environmentalPeriods || [])].filter((p) =>
        doDateRangesOverlap(rangeStart, rangeEnd, p.startDate, p.endDate)
      );
      const uniqueSortedPeriodsForRange = uniqWith(
        sortBy(
          periodsOverlappingRange.map((p) => pick(p, ['startDate', 'endDate'])),
          (v) => date(v.endDate).valueOf()
        ),
        isEqual
      );

      // Create segments within [rangeStart, rangeEnd]. Match contract period: prefer SAME periodName; fallback to any.
      const segments: CalculatedProjectOfferPeriod[] = uniqueSortedPeriodsForRange.reduce((prev, cur) => {
        if (!doDateRangesOverlap(rangeStart, rangeEnd, cur.startDate, cur.endDate)) return prev;
        if (
          prev.length > 0 &&
          (date(prev[prev.length - 1].endDate).valueOf() >= date(rangeEnd).valueOf() ||
            date(prev[prev.length - 1].endDate).valueOf() >= date(cur.endDate).valueOf())
        ) {
          return prev;
        }

        const segmentStartDate =
          prev.length > 0
            ? formatApiDate(date(prev[prev.length - 1].endDate).add(1, 'day'))
            : formatApiDate(getEarlierDate(cur.startDate, rangeStart));
        const finalSegmentStart =
          date(segmentStartDate).valueOf() < date(rangeStart).valueOf() ? rangeStart : segmentStartDate;
        const segmentEndDate = date(cur.endDate).valueOf() > date(rangeEnd).valueOf() ? rangeEnd : cur.endDate;

        if (date(finalSegmentStart).valueOf() >= date(segmentEndDate).valueOf()) return prev;

        // Split segment by calendar year so env rate (MAT_CERT/CAL_CERT) uses one year's lookup per sub-segment, no weighting.
        const subRanges = splitSegmentByCalendarYear(finalSegmentStart, segmentEndDate);
        let nextPrev = prev;

        for (const sub of subRanges) {
          // Prefer contract period from the current periodName group if it covers this sub-segment (so e.g. 24M
          // output uses 24M contract/env for the whole range from rangeStart, not 12M where 12M would be "smallest").
          const coveringFromGroup = periodsInGroup.find((p) => isDateBetween(sub.end, p.startDate, p.endDate));
          const coveringPeriods = contractPeriods.filter((p) => isDateBetween(sub.end, p.startDate, p.endDate));
          const contractPeriodToUse =
            coveringFromGroup ?? sortBy(coveringPeriods, (p) => p.periodName ?? 999)[0] ?? basePeriod;

          const overlappingEnvPeriods = findOverlappingEnvironmentalPeriods(
            { ...contractPeriodToUse, startDate: sub.start, endDate: sub.end },
            environmentalPeriods
          );
          const matchingEnvPeriod = findBestMatchingEnvironmentalPeriod(
            { start: sub.start, end: sub.end },
            overlappingEnvPeriods,
            contractPeriodToUse.periodName
          );

          const mergedPeriod = mergeContractAndEnvironmentalRates(contractPeriodToUse, matchingEnvPeriod, {
            start: sub.start,
            end: sub.end
          });

          const calculatedSegment = calculatePeriodWithCosts(offer, mergedPeriod);
          nextPrev = [...nextPrev, calculatedSegment] as CalculatedProjectOfferPeriod[];
        }

        return nextPrev;
      }, [] as CalculatedProjectOfferPeriod[]);

      const isBest = periodIndexPairs.some(({ period }) => (period as any).isBest === true);
      const segmentsToUse = segments;

      // Aggregate cost fields from filtered segments
      const {
        periodContestableCosts,
        periodRenewalCosts,
        periodEnergyCosts,
        periodRegulatedCost,
        periodMeteringCharge,
        periodNetworkCost,
        periodTotalCosts,
        periodRetailerServiceFee,
        totalNumOfDays,
        meterContestableCosts,
        meterEnergyCosts,
        meterRenewableCosts,
        meterRetailerServiceFee,
        meterMeteringCharge
      } = aggregatePeriodCosts(segmentsToUse);

      // Total KWH (with loss) = sum of per-meter consumption with dlf×mlf
      // Cost (c/kWh) = Contestable Total Spend / Total KWH (with loss)
      const totalKwhWithLoss = sumBy(projectMeters, (m) =>
        calculateMeterConsumption(m, totalNumOfDays, { usePerMeterLossFactor: true })
      );
      const totalConsumption = sumBy(
        projectMeters,
        (m) => (m.annualData?.totalEnergy ?? 0) * (totalNumOfDays / DAYS_PER_YEAR)
      );
      const averageDailyPeakEnergy = sumBy(projectMeters, (v) => calculateDailyEnergy(v.annualData.peakEnergy));
      const averageDailyShoulderEnergy = sumBy(projectMeters, (v) => calculateDailyEnergy(v.annualData.shoulderEnergy));
      const averageDailyOffPeakEnergy = sumBy(projectMeters, (v) => calculateDailyEnergy(v.annualData.offPeakEnergy));

      // Calculate annual and average costs (use actual days, not periodName)
      // Cost (c/kWh) = Contestable Total Spend / Total KWH (with loss)
      const annualTotalCosts = totalNumOfDays > 0 ? (periodTotalCosts / totalNumOfDays) * DAYS_PER_YEAR : 0;
      const avgCostPerKwh = totalKwhWithLoss > 0 ? (periodTotalCosts / totalKwhWithLoss) * 100 : 0;
      const avgContestableCostsPerKwh = totalKwhWithLoss > 0 ? (periodContestableCosts / totalKwhWithLoss) * 100 : 0;

      // Calculate weighted environmental rates from filtered segments
      const environmentalRateFields = calculateWeightedEnvironmentalRates(segmentsToUse);

      // Rate Unit (display): weighted average by numOfDays only so all segments contribute.
      // Formula: rate = Σ(seg.rate × seg.numOfDays) / Σ(seg.numOfDays)
      const totalDaysForRate = sumBy(segmentsToUse, (s) => s.numOfDays ?? 0);
      const peak =
        totalDaysForRate > 0
          ? sumBy(segmentsToUse, (s) => (s.peak ?? 0) * (s.numOfDays ?? 0)) / totalDaysForRate
          : basePeriod.peak ?? 0;
      const offPeak =
        totalDaysForRate > 0
          ? sumBy(segmentsToUse, (s) => (s.offPeak ?? 0) * (s.numOfDays ?? 0)) / totalDaysForRate
          : basePeriod.offPeak ?? 0;
      const shoulder =
        totalDaysForRate > 0
          ? sumBy(segmentsToUse, (s) => (s.shoulder ?? 0) * (s.numOfDays ?? 0)) / totalDaysForRate
          : basePeriod.shoulder ?? 0;

      // Period date range: start = max(derivedStart, baseStart), end = rangeEnd; never before contract start
      const finalStartDate = rangeStart;
      const finalEndDate = rangeEnd;

      // Exclude peak/offPeak/shoulder from baseRest so computed weighted rates are used
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- destructure only to omit from baseRest
      const { peak: _basePeak, offPeak: _baseOff, shoulder: _baseSh, ...baseRest } = basePeriod as any;
      return {
        ...baseRest,
        periodName,
        startDate: finalStartDate,
        endDate: finalEndDate,
        isBest, // Preserve isBest flag from any period with this periodName
        periodContestableCosts,
        periodRenewalCosts,
        periodRegulatedCost,
        periodEnergyCosts,
        periodMeteringCharge,
        periodRetailerServiceFee,
        annualTotalCosts,
        totalNumOfDays,
        totalConsumption,
        totalConsumptionIncLoss: totalKwhWithLoss,
        avgContestableCostsPerKwh,
        avgCostPerKwh,
        periodNetworkCost,
        periodTotalCosts,
        environmentalRatePeriods: segmentsToUse,
        meterContestableCosts,
        meterEnergyCosts,
        meterRenewableCosts,
        meterRetailerServiceFee,
        meterMeteringCharge,
        ...environmentalRateFields,
        peak,
        offPeak,
        shoulder,
        // For merge in aggregateForDisplay: weighted rate across multiple meter groups uses same formula
        averageDailyPeakEnergy,
        averageDailyOffPeakEnergy,
        averageDailyShoulderEnergy
      } as CalculatedProjectOfferPeriodsByName;
    });

    return {
      ...offer,
      isBest: rawOffer.isBest || false,
      dailyMeteringFee: offer.dailyMeteringFee,
      dailySuppDMAOrRPFee: offer.dailySuppDMAOrRPFee,
      dailyServiceFee: offer.dailyServiceFee,
      periods: reverse(uniqBy(reverse(periods), 'periodName'))
    } as CalculatedProjectOffer;
  });

  return projectOffers;
}

/**
 * Aggregate calculated offers for display: one row per (retailer, periodName).
 * For each (retailer, periodName): picks min(periodContestableCosts) when single period;
 * when multiple periods (mix-and-match with disjoint meterIds), merges by summing costs.
 * When meterOfferPeriodMapping is provided and meters overlap across periods, uses each
 * meter's recommended period to avoid double-counting.
 * When useFullGroupCostsForPeriods is true (e.g. merged group for Best Offers / Contract Term Comparison),
 * keeps each period's full-group costs instead of subsetting to meters that selected this period.
 */
export function aggregateForDisplay(
  calculatedOffers: CalculatedProjectOffer[],
  recommendedPeriodIdOrIds: string | string[],
  projectMeters?: ReportProjectMeter[],
  meterOfferPeriodMapping?: Record<string, { recommendedPeriodId: string }>,
  options?: { useFullGroupCostsForPeriods?: boolean }
): CalculatedProjectOffer[] {
  const recommendedPeriodIds = Array.isArray(recommendedPeriodIdOrIds)
    ? recommendedPeriodIdOrIds
    : recommendedPeriodIdOrIds
    ? [recommendedPeriodIdOrIds]
    : [];
  if (!calculatedOffers?.length) return [];

  const pairs = flatMap(calculatedOffers, (offer) => (offer.periods || []).map((period) => ({ offer, period })));
  const key = (o: { offer: CalculatedProjectOffer; period: CalculatedProjectOfferPeriodsByName }) =>
    `${o.offer.retailerId || (o.offer as any).retailer?.id || ''}-${o.period.periodName}`;
  const byRetailerPeriod = groupBy(pairs, key);

  const bestByRetailerPeriod: { retailerId: string; period: CalculatedProjectOfferPeriodsByName }[] = [];
  Object.entries(byRetailerPeriod).forEach(([, group]) => {
    if (group.length === 0) return;
    const retailerId = group[0].offer.retailerId || (group[0].offer as any).retailer?.id || '';

    let mergedPeriod: CalculatedProjectOfferPeriodsByName;
    if (group.length === 1) {
      const p = group[0].period;
      // When useFullGroupCostsForPeriods is true, keep full-group totals (for Best Offers / Contract Term Comparison).
      // Otherwise when meterOfferPeriodMapping is provided, filter to only meters that selected this period.
      const periodId = p.id;
      const selectedMeterIds =
        meterOfferPeriodMapping && periodId && !options?.useFullGroupCostsForPeriods
          ? (Object.keys(p.meterContestableCosts ?? {}) as string[]).filter(
              (meterId) => meterOfferPeriodMapping[meterId]?.recommendedPeriodId === periodId
            )
          : (Object.keys(p.meterContestableCosts ?? {}) as string[]);

      if (
        !options?.useFullGroupCostsForPeriods &&
        selectedMeterIds.length > 0 &&
        selectedMeterIds.length < (Object.keys(p.meterContestableCosts ?? {}).length || 0)
      ) {
        const meterContestableCosts = selectedMeterIds.reduce((acc, id) => {
          const v = (p.meterContestableCosts ?? {})[id];
          if (v != null) acc[id] = v;
          return acc;
        }, {} as Record<string, number>);
        const meterEnergyCosts = selectedMeterIds.reduce((acc, id) => {
          const v = (p.meterEnergyCosts ?? {})[id];
          if (v != null) acc[id] = v;
          return acc;
        }, {} as Record<string, number>);
        const meterRenewableCosts = selectedMeterIds.reduce((acc, id) => {
          const v = (p.meterRenewableCosts ?? {})[id];
          if (v != null) acc[id] = v;
          return acc;
        }, {} as Record<string, number>);
        const meterRetailerServiceFee = selectedMeterIds.reduce((acc, id) => {
          const v = (p.meterRetailerServiceFee ?? {})[id];
          if (v != null) acc[id] = v;
          return acc;
        }, {} as Record<string, number>);
        const meterMeteringCharge = selectedMeterIds.reduce((acc, id) => {
          const v = (p.meterMeteringCharge ?? {})[id];
          if (v != null) acc[id] = v;
          return acc;
        }, {} as Record<string, number>);
        const sumRecordValues = (r: Record<string, number>) => Object.values(r).reduce((a, b) => a + b, 0);
        const contestableDedup = sumRecordValues(meterContestableCosts);
        const totalNumOfDays = p.totalNumOfDays ?? p.numOfDays ?? 0;
        const totalKwhWithLoss =
          projectMeters?.length && selectedMeterIds.length > 0
            ? sumBy(
                projectMeters.filter((m) => selectedMeterIds.includes(m.id)),
                (m) => calculateMeterConsumption(m, totalNumOfDays, { usePerMeterLossFactor: true })
              )
            : (p as any).totalConsumptionIncLoss ?? 0;
        mergedPeriod = {
          ...p,
          periodContestableCosts: contestableDedup,
          periodEnergyCosts: sumRecordValues(meterEnergyCosts),
          periodRenewalCosts: sumRecordValues(meterRenewableCosts),
          periodRetailerServiceFee: sumRecordValues(meterRetailerServiceFee),
          periodMeteringCharge: sumRecordValues(meterMeteringCharge),
          meterContestableCosts,
          meterEnergyCosts,
          meterRenewableCosts,
          meterRetailerServiceFee,
          meterMeteringCharge,
          totalConsumptionIncLoss: totalKwhWithLoss,
          avgContestableCostsPerKwh:
            totalKwhWithLoss > 0 && contestableDedup > 0
              ? (contestableDedup / totalKwhWithLoss) * 100
              : (p as any).avgContestableCostsPerKwh ?? 0,
          isBest: recommendedPeriodIds.length > 0 && recommendedPeriodIds.includes(p.id ?? '')
        } as CalculatedProjectOfferPeriodsByName;
      } else {
        mergedPeriod = {
          ...p,
          isBest: recommendedPeriodIds.length > 0 && recommendedPeriodIds.includes(p.id ?? '')
        } as CalculatedProjectOfferPeriodsByName;
      }
    } else {
      // Mix-and-match: merge multiple periods. When meterOfferPeriodMapping provided and meters overlap,
      // use each meter's recommended period to avoid double-counting; otherwise sum (disjoint meterIds)
      const first = group[0].period;
      const mergeMeterRecord = (
        key:
          | 'meterContestableCosts'
          | 'meterEnergyCosts'
          | 'meterRenewableCosts'
          | 'meterRetailerServiceFee'
          | 'meterMeteringCharge'
      ): Record<string, number> => {
        const allMeterIds = uniq(group.flatMap((g) => Object.keys(g.period[key] ?? {})));
        const result: Record<string, number> = {};
        allMeterIds.forEach((meterId: string) => {
          const recId = meterOfferPeriodMapping?.[meterId]?.recommendedPeriodId;
          const periodWithValue = recId ? group.find((g) => g.period.id === recId) : null;
          const cost = periodWithValue ? (periodWithValue.period[key] as Record<string, number>)?.[meterId] : undefined;
          if (cost != null) {
            result[meterId] = cost;
          } else {
            const anyPeriod = group.find((g) => (g.period[key] as Record<string, number>)?.[meterId] != null);
            if (anyPeriod) {
              result[meterId] = (anyPeriod.period[key] as Record<string, number>)[meterId];
            }
          }
        });
        return result;
      };
      const meterContestableCosts = mergeMeterRecord('meterContestableCosts');
      const meterEnergyCosts = mergeMeterRecord('meterEnergyCosts');
      const meterRenewableCosts = mergeMeterRecord('meterRenewableCosts');
      const meterRetailerServiceFee = mergeMeterRecord('meterRetailerServiceFee');
      const meterMeteringCharge = mergeMeterRecord('meterMeteringCharge');
      const sumRecord = (r: Record<string, number>) => Object.values(r).reduce((a, b) => a + b, 0);
      const contestableDedup = sumRecord(meterContestableCosts);
      const contestableSum = sumBy(group, (g) => g.period.periodContestableCosts ?? 0);
      const regulatedSum = sumBy(group, (g) => g.period.periodRegulatedCost ?? 0);
      const networkSum = sumBy(group, (g) => g.period.periodNetworkCost ?? 0);
      const scale = contestableSum > 0 ? contestableDedup / contestableSum : 1;

      const totalConsumptionMerged = sumBy(group, (g) => g.period.totalConsumption ?? 0);

      // Per-meter rates for UI: when same retailer/term has different rates per meter, show one line per meter.
      const getPeriodRate = (p: CalculatedProjectOfferPeriodsByName, key: 'peak' | 'offPeak' | 'shoulder') => {
        const seg = last(p.environmentalRatePeriods ?? []) as
          | { peak?: number; offPeak?: number; shoulder?: number }
          | undefined;
        return seg?.[key] ?? (p as any)[key] ?? 0;
      };
      const meterPeakRates: Record<string, number> = {};
      const meterOffPeakRates: Record<string, number> = {};
      const meterShoulderRates: Record<string, number> = {};
      Object.keys(meterContestableCosts).forEach((meterId: string) => {
        const recId = meterOfferPeriodMapping?.[meterId]?.recommendedPeriodId;
        const g = recId ? group.find((gg) => gg.period.id === recId) : null;
        const p = g?.period;
        if (p) {
          meterPeakRates[meterId] = getPeriodRate(p, 'peak');
          meterOffPeakRates[meterId] = getPeriodRate(p, 'offPeak');
          meterShoulderRates[meterId] = getPeriodRate(p, 'shoulder');
        }
      });

      mergedPeriod = {
        ...first,
        periodContestableCosts: contestableDedup,
        periodEnergyCosts: sumRecord(meterEnergyCosts),
        periodRenewalCosts: sumRecord(meterRenewableCosts),
        periodRegulatedCost: regulatedSum * scale,
        periodMeteringCharge: sumRecord(meterMeteringCharge),
        periodRetailerServiceFee: sumRecord(meterRetailerServiceFee),
        periodNetworkCost: networkSum * scale,
        periodTotalCosts: contestableDedup + regulatedSum * scale + networkSum * scale,
        annualTotalCosts: sumBy(group, (g) => g.period.annualTotalCosts ?? 0),
        totalConsumption: totalConsumptionMerged,
        meterContestableCosts,
        meterEnergyCosts,
        meterRenewableCosts,
        meterRetailerServiceFee,
        meterMeteringCharge,
        meterPeakRates: Object.keys(meterPeakRates).length > 0 ? meterPeakRates : undefined,
        meterOffPeakRates: Object.keys(meterOffPeakRates).length > 0 ? meterOffPeakRates : undefined,
        meterShoulderRates: Object.keys(meterShoulderRates).length > 0 ? meterShoulderRates : undefined,
        isBest: recommendedPeriodIds.length > 0 && group.some((g) => recommendedPeriodIds.includes(g.period.id ?? ''))
      } as CalculatedProjectOfferPeriodsByName;

      // Merged Rate Unit (reverse method per meter): same formula as calculateRateUnitReverseFromSegments
      const mergedMeterIdsForRate = Object.keys(meterContestableCosts);
      const mergedRates =
        projectMeters?.length && group.length > 0
          ? calculateEffectiveRateUnitWeightedAvg(
              group,
              projectMeters,
              (m, n, opts) => calculateMeterConsumptionByComponent(m as ReportProjectMeter, n, opts),
              meterOfferPeriodMapping,
              mergedMeterIdsForRate
            )
          : calculateRateUnitWeightedAvgFromMergedPeriods(group.map((g) => g.period));
      mergedPeriod.peak = mergedRates.peak > 0 ? mergedRates.peak : first.peak ?? 0;
      mergedPeriod.offPeak = mergedRates.offPeak > 0 ? mergedRates.offPeak : first.offPeak ?? 0;
      mergedPeriod.shoulder = mergedRates.shoulder > 0 ? mergedRates.shoulder : first.shoulder ?? 0;

      const totalContestable = mergedPeriod.periodContestableCosts ?? 0;
      const totalNumOfDays = mergedPeriod.totalNumOfDays ?? first.totalNumOfDays ?? 0;
      const mergedMeterIds = Object.keys(mergedPeriod.meterContestableCosts || {});
      const totalKwhWithLoss =
        projectMeters?.length && mergedMeterIds.length > 0
          ? sumBy(
              projectMeters.filter((m) => mergedMeterIds.includes(m.id)),
              (m) => calculateMeterConsumption(m, totalNumOfDays, { usePerMeterLossFactor: true })
            )
          : group.reduce((sum, g) => {
              const c = g.period.periodContestableCosts ?? 0;
              const avg = (g.period as any).avgContestableCostsPerKwh ?? 0;
              return sum + (avg > 0 ? (c / avg) * 100 : 0);
            }, 0);
      mergedPeriod.totalConsumptionIncLoss = totalKwhWithLoss;
      (mergedPeriod as any).avgContestableCostsPerKwh =
        totalKwhWithLoss > 0
          ? (totalContestable / totalKwhWithLoss) * 100
          : (group[0].period as any).avgContestableCostsPerKwh ?? 0;
    }

    bestByRetailerPeriod.push({ retailerId, period: mergedPeriod });
  });

  const byRetailer = groupBy(bestByRetailerPeriod, 'retailerId');
  const retailerIds = Object.keys(byRetailer);
  const groupMeterIds = projectMeters?.map((m) => m.id) ?? [];
  // Single source of truth: only include (retailer, term) where EVERY meter in the group has that retailer's offer for that term (merged period's meterContestableCosts contains all group meters). Avoids UI relying on firstOffer.meterIds.
  const displayOffers: CalculatedProjectOffer[] = retailerIds
    .map((retailerId) => {
      const firstOffer = calculatedOffers.find((o) => (o.retailerId || (o as any).retailer?.id) === retailerId);
      if (!firstOffer) throw new Error(`No offer for retailer ${retailerId}`);
      const periods = sortBy(
        byRetailer[retailerId]
          .map((p) => p.period)
          .filter(
            (p) =>
              groupMeterIds.length === 0 || groupMeterIds.every((mid) => (p.meterContestableCosts ?? {})[mid] != null)
          ),
        'periodName'
      );
      return {
        ...firstOffer,
        isBest: periods.some((p) => p.isBest),
        periods
      } as CalculatedProjectOffer;
    })
    .filter((offer) => (offer.periods || []).length > 0);

  return displayOffers;
}
